import { createContext, useCallback, useContext, useState } from "react";
import Slide from "@mui/material/Slide";
import Snackbar from "@mui/material/Snackbar";

import Alert from "features/components/Alert";

import {
  ToastContextType,
  ToastMessage,
  ToastOptions,
  ToastVariant,
} from "./types";

const ToastContext = createContext<ToastContextType>({
  notify: () => {},
});

let idCounter = 0;

const handleAutoCloseValue = (autoClose: number | undefined | null) => {
  if (autoClose === null) {
    return null;
  }

  if (autoClose === undefined) {
    return 5000;
  }

  return autoClose;
};

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [queue, setQueue] = useState<ToastMessage[]>([]);

  const notify = useCallback(
    (message: React.ReactNode, variant: ToastVariant, opts?: ToastOptions) => {
      setQueue((prev) => [
        ...prev,
        {
          id: ++idCounter,
          message,
          variant,
          autoClose: handleAutoCloseValue(opts?.autoClose),
          position: opts?.position ?? {
            vertical: "top",
            horizontal: "right",
          },
          className: opts?.className,
        },
      ]);
    },
    []
  );

  const handleClose = (id: number) => {
    setQueue((prev) => prev.filter((t) => t.id !== id));
  };

  const current = queue[0];

  return (
    <ToastContext.Provider value={{ notify }}>
      {children}
      {current && (
        <Snackbar
          key={current.id}
          open
          autoHideDuration={current.autoClose}
          onClose={(_, reason) => {
            if (reason === "clickaway") return;

            handleClose(current.id);
          }}
          anchorOrigin={current.position}
          className={current.className}
          slots={{
            transition: Slide,
          }}
        >
          <Alert
            onClose={() => handleClose(current.id)}
            severity={current.variant}
            className="w-full"
          >
            {current.message}
          </Alert>
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToastContext must be used within ToastProvider");
  }

  return ctx;
};
